import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Stack,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Public as PublicIcon,
  PublicOff as PublicOffIcon,
} from "@mui/icons-material";
import {
  getInvitationStatusDisplay,
  formatDate,
} from "../utils/invitationHelpers";
import { STATUS_OPTIONS } from "../types/invitationTypes";

const InvitationList = ({
  invitations = [],
  onSelect,
  onDelete,
  onPublish,
  onUnpublish,
  filters = {},
  onFilterChange,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters?.search || "");

  const handleSearch = (e) => {
    e.preventDefault();
    onFilterChange && onFilterChange({ search: searchTerm });
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    onFilterChange && onFilterChange({ search: "", status: "all" });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!invitations || invitations.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No invitations found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Create your first wedding invitation to get started
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <form onSubmit={handleSearch} style={{ flex: 1, minWidth: 200 }}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search invitations..."
              InputProps={{
                endAdornment: (
                  <IconButton type="submit" size="small">
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </form>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters?.status || "all"}
              onChange={(e) => onFilterChange({ status: e.target.value })}
              label="Status"
            >
              <MenuItem value="all">All Status</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
            size="small"
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {invitations.map((inv) => {
          const statusDisplay = getInvitationStatusDisplay(inv.status);
          return (
            <Grid item xs={12} sm={6} md={4} key={inv.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                    cursor: "pointer",
                  },
                }}
                onClick={() => onSelect && onSelect(inv.id)}
              >
                <Box
                  sx={{
                    position: "relative",
                    pt: "56.25%",
                    bgcolor: "grey.200",
                  }}
                >
                  {inv.images?.coverImage ? (
                    <CardMedia
                      component="img"
                      image={inv.images.coverImage}
                      alt={inv.invitationDetails?.title}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: inv.template?.primaryColor || "grey.300",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: inv.template?.secondaryColor || "white" }}
                      >
                        No Cover
                      </Typography>
                    </Box>
                  )}
                  <Chip
                    label={statusDisplay.label}
                    color={statusDisplay.color}
                    size="small"
                    sx={{ position: "absolute", top: 8, right: 8 }}
                  />
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" noWrap>
                    {inv.invitationDetails?.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {inv.invitationDetails?.groomName} &{" "}
                    {inv.invitationDetails?.brideName}
                  </Typography>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mt: 2 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(inv.invitationDetails?.eventDate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {inv.template?.templateName}
                    </Typography>
                  </Stack>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ mt: 1 }}
                  >
                    {inv.invitationDetails?.venueName}
                  </Typography>

                  <Box
                    sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        fullWidth
                        onClick={() => onSelect && onSelect(inv.id)}
                        startIcon={<ViewIcon />}
                      >
                        View
                      </Button>
                      {inv.status === "published" ? (
                        <Tooltip title="Unpublish">
                          <IconButton
                            color="warning"
                            size="small"
                            onClick={() => onUnpublish && onUnpublish(inv.id)}
                          >
                            <PublicOffIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Publish">
                          <IconButton
                            color="success"
                            size="small"
                            onClick={() => onPublish && onPublish(inv.id)}
                          >
                            <PublicIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => onDelete && onDelete(inv.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default InvitationList;
